// src/pages/BookingDetails.jsx
import { useState, useEffect } from "react";
import {
  Button,
  Text,
  Title,
  Paper,
  Stack,
  Group,
  Select,
  Checkbox,
  Box,
  Anchor,
  Alert,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBooking } from "../context/BookingContext";
import dayjs from "dayjs";
import { Modal, ScrollArea } from "@mantine/core";
const VEHICLE_TYPES = [
  { value: "bike", label: "🏍️ Bike" },
  { value: "scooter", label: "🛵 Scooty" },
  { value: "car", label: "🚗 Car" },
  { value: "cycle", label: "🚲 Cycle" },
];


export default function BookingDetails() {
  const navigate = useNavigate();



  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { bookingData, setBookingData } = useBooking();
  const customer = bookingData.customer;
  const [submittedData, setSubmittedData] = useState(null);
  const shopKey = bookingData.shopKey;
  const [showTerms, setShowTerms] = useState(false);

  const { vehicleType, pickupTime, dropTime } = bookingData.booking || {};


  useEffect(() => {
    if (!bookingData.booking?.pickupTime) {
      const now = new Date();
      const nextDay = dayjs(now).add(1, "day").toDate();

      setBookingData(prev => ({
        ...prev,
        booking: {
          ...prev.booking,
          pickupTime: now,
          dropTime: nextDay,
        },
      }));
    }
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!vehicleType) newErrors.vehicleType = "Please select a vehicle type";
    if (!pickupTime) newErrors.pickupTime = "Pickup time is required";
    if (!dropTime) newErrors.dropTime = "Drop time is required";

    const pickupDate = new Date(pickupTime);
    const dropDate = new Date(dropTime);

    if (
      pickupDate &&
      dropDate &&
      !isNaN(pickupDate.getTime()) &&
      !isNaN(dropDate.getTime()) &&
      dropDate <= pickupDate
    ) {
      newErrors.dropTime = "Drop time must be after pickup time";
    }

    if (!agreedToTerms) newErrors.terms = "Please agree to terms and conditions";

    return newErrors;
  };



  useEffect(() => {
    if (!submitted) {
      if (
        !shopKey ||
        !customer?.name ||
        !customer?.mobile
      ) {
        navigate("/?shopKey=" + shopKey);
      }
    }
  }, [shopKey, customer, submitted, navigate]);

  const handleBack = () => {
    navigate("/?shopKey=" + shopKey);
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const { pickupTime, dropTime, vehicleType } = bookingData.booking;

      const pickupDate = new Date(pickupTime);
      const dropDate = new Date(dropTime);
      if (!(pickupDate instanceof Date) || !(dropDate instanceof Date)) {
        setErrors({ submit: "Invalid pickup or drop time selected." });
        setLoading(false);
        return;
      }

      const formData = new FormData();

      // ✅ customer
      formData.append("name", customer.name);
      formData.append("phoneNumber", customer.mobile);
      formData.append("email", customer.email || "");

      // ✅ booking (FROM CONTEXT ONLY)
      formData.append("vehicleType", vehicleType);
      formData.append("pickupTime", dayjs(pickupDate).format("YYYY-MM-DD HH:mm:ss"));
      formData.append("dropTime", dayjs(dropDate).format("YYYY-MM-DD HH:mm:ss"));

      // ✅ meta
      formData.append("shopKey", shopKey);


      // ✅ files
      if (customer?.dlDocument) {
        formData.append("dlFile", customer.dlDocument);
      }

      if (customer?.aadhaarDocument) {
        formData.append("aadhaarFile", customer.aadhaarDocument);
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/customers`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setSubmittedData({
        customer: { ...customer },
        booking: { ...bookingData.booking },
      });

      setSubmitted(true);
      setTimeout(() => {
        setBookingData(prev => ({
          ...prev,
          customer: {
            name: "",
            mobile: "",
            email: "",
            dlDocument: null,
            aadhaarDocument: null,
          },
          booking: {
            vehicleType: "bike",
            pickupTime: null,
            dropTime: null,
          },
        }));
      }, 100);

    } catch (err) {
      console.log("Submission error:", err?.response?.data || err.message);
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box
        style={{
          height: "100vh", // 🔥 use height instead of minHeight
          backgroundColor: "#f0ede8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
        }}
      >
        <Paper
          radius={18}
          p={24} // 🔥 reduced from 40
          shadow="sm"
          bg="white"
          style={{
            maxWidth: 380,
            width: "100%",
            textAlign: "center",
          }}
        >


          <Title order={3} fw={800} mb={6}>
            Request Submitted!
          </Title>

          <Text c="#666" size="sm" mb={20}>
            Your booking request has been sent. The shop owner will contact you shortly.
          </Text>

          <Box
            style={{
              backgroundColor: "#f5f2ed",
              borderRadius: 12,
              padding: 12,
              textAlign: "left",
            }}
          >
            <Text size="xs" fw={600} c="#777" mb={6}>
              Booking Summary
            </Text>

            <Text size="sm">📋 {submittedData?.customer?.name}</Text>
            <Text size="sm">📱 {submittedData?.customer?.mobile}</Text>

            <Text size="sm">🚗 {submittedData?.booking?.vehicleType}</Text>
            <Text size="sm">
              📅 Pickup: {new Date(submittedData?.booking?.pickupTime).toLocaleString()}
            </Text>
            <Text size="sm">
              📅 Drop: {new Date(submittedData?.booking?.dropTime).toLocaleString()}
            </Text>
          </Box>

          <Button
            fullWidth
            mt={20}
            radius={12}
            onClick={() => navigate(`/?shopKey=${shopKey}`)}
            style={{
              backgroundColor: "#f24c1a",
              fontWeight: 600,
            }}
          >
            Go Home
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: "100vh",
        backgroundColor: "#f0ede8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Box style={{ width: "100%", maxWidth: 480 }}>

        {/* Header */}
        <Stack align="center" mb={32} gap={4}>
          <Box
            style={{
              width: 60,
              height: 60,
              borderRadius: 12,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
            }}
          >
            <img
              src="/rentalflow.png"
              alt="RentalFlow"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Title order={2} fw={800} c="#1a1a1a">Booking Details</Title>
          <Text c="#888" size="sm">Almost there! Fill in your booking info</Text>
        </Stack>

        {/* Progress */}
        <Group mb={24} gap={8}>
          <Box style={{ flex: 1, height: 4, backgroundColor: "#f24c1a", borderRadius: 4 }} />
          <Box style={{ flex: 1, height: 4, backgroundColor: "#f24c1a", borderRadius: 4 }} />
          <Text size="xs" c="#888">Step 2 of 2</Text>
        </Group>

        {/* Customer summary */}
        <Paper radius={18} p={16} shadow="xs" bg="white" mb={16}>
          <Group>
            <Box
              style={{
                width: 40, height: 40,
                backgroundColor: "#f24c1a",
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text fw={800} c="white" size="sm">
                {customer?.name?.split(" ").map(n => n[0]).join("").toUpperCase()}
              </Text>
            </Box>
            <Box>
              <Text fw={700} size="sm" c="#1a1a1a">{customer?.name}</Text>
              <Text size="xs" c="#888">{customer?.mobile}</Text>
            </Box>
            <Button mt={10} variant="light" onClick={handleBack}>
              Edit
            </Button>
          </Group>
        </Paper>

        <Paper radius={18} p={24} shadow="xs" bg="white">
          <Title order={4} fw={800} mb={20} c="#1a1a1a">Vehicle & Schedule</Title>

          <Stack gap={16}>
            {/* Vehicle Type */}
            <Select
              label="Vehicle Type"
              placeholder="Select vehicle type"
              data={VEHICLE_TYPES}
              value={bookingData.booking.vehicleType}
              onChange={(val) => {
                setErrors({ ...errors, vehicleType: "" });

                setBookingData(prev => ({
                  ...prev,
                  booking: {
                    ...prev.booking,
                    vehicleType: val,
                  },
                }));
              }}
              error={errors.vehicleType}
              required
              styles={{
                input: { borderRadius: 10, backgroundColor: "#f5f2ed", border: "none" },
                label: { fontWeight: 600, fontSize: 12, color: "#555" },
              }}
            />

            {/* Pickup Time */}
            <DateTimePicker
              label="Pickup Time"
              placeholder="Select pickup date & time"
              value={bookingData.booking.pickupTime}
              onChange={(val) => {
                setErrors({ ...errors, pickupTime: "" });

                setBookingData(prev => ({
                  ...prev,
                  booking: {
                    ...prev.booking,
                    pickupTime: val,
                  },
                }));
              }}
              error={errors.pickupTime}
              required
              minDate={new Date()}
              styles={{
                input: { borderRadius: 10, backgroundColor: "#f5f2ed", border: "none" },
                label: { fontWeight: 600, fontSize: 12, color: "#555" },
              }}
            />

            {/* Drop Time */}
            <DateTimePicker
              label="Drop Time"
              placeholder="Select drop date & time"
              value={bookingData.booking.dropTime}
              onChange={(val) => {
                setErrors({ ...errors, dropTime: "" });

                setBookingData(prev => ({
                  ...prev,
                  booking: {
                    ...prev.booking,
                    dropTime: val,
                  },
                }));
              }}
              error={errors.dropTime}
              required
              minDate={bookingData.booking.pickupTime || new Date()}
              styles={{
                input: { borderRadius: 10, backgroundColor: "#f5f2ed", border: "none" },
                label: { fontWeight: 600, fontSize: 12, color: "#555" },
              }}
            />

            {/* Documents status */}
            <Box
              style={{
                backgroundColor: "#f5f2ed",
                borderRadius: 12,
                padding: 12,
              }}
            >
              <Text size="sm" fw={600} c="#555" mb={8}>Documents</Text>
              <Group gap={8}>
                <Text size="xs" c="#2e7d32">✓ Driving Licence uploaded</Text>
                <Text size="xs" c="#2e7d32">✓ Aadhaar Card uploaded</Text>
              </Group>
            </Box>

            {/* Terms */}
            <Box>
              <Checkbox
                checked={agreedToTerms}
                onChange={() => {
                  // if already agreed, allow uncheck
                  if (agreedToTerms) {
                    setAgreedToTerms(false);
                  } else {
                    // open terms modal first
                    setShowTerms(true);
                  }
                }}
                label={
                  <Text size="sm" c="#555">
                    I agree to the Terms & Conditions
                  </Text>
                }
                color="orange"
                styles={{
                  input: {
                    cursor: "pointer",
                  },
                }}
              />

              {errors.terms && (
                <Text size="xs" c="red" mt={4}>
                  {errors.terms}
                </Text>
              )}
            </Box>

            {/* Submit error */}
            {errors.submit && (
              <Alert color="red" radius={10}>
                {errors.submit}
              </Alert>
            )}
          </Stack>
        </Paper>

        {/* Submit Button */}
        <Button
          fullWidth
          mt={20}
          h={52}
          radius={14}
          loading={loading}
          onClick={handleSubmit}
          style={{
            backgroundColor: "#f24c1a",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          Submit Booking Request →
        </Button>

        <Text size="xs" c="#aaa" ta="center" mt={16}>
          The shop owner will review and confirm your booking
        </Text>

<Modal
  opened={showTerms}
  onClose={() => setShowTerms(false)}
  title="Rental Terms & Conditions"
  centered
  size="lg"
  radius={16}
  scrollAreaComponent={ScrollArea.Autosize}
>
  <Stack gap={12}>

    <Text size="sm">
      • You are responsible for damages, challans, and penalties.
    </Text>

    <Text size="sm">
      • Fuel must be returned at the same level.
    </Text>

    <Text size="sm">
      • Late return charges apply.
    </Text>

    <Text size="sm">
      • Only the verified rider may operate the vehicle.
    </Text>

    <Text size="sm">
      • Original DL and ID proof are mandatory during pickup.
    </Text>

    <Text size="sm">
      • Drunk driving is strictly prohibited.
    </Text>

    <Text size="sm">
      • Helmet damage/loss charges may apply.
    </Text>

    <Button
      mt={16}
      radius={12}
      style={{
        backgroundColor: "#f24c1a",
      }}
      onClick={() => {
        setAgreedToTerms(true);

        setErrors(prev => ({
          ...prev,
          terms: "",
        }));

        setShowTerms(false);
      }}
    >
      I Read & Agree
    </Button>
  </Stack>
</Modal>
      </Box>
    </Box>
  );
}